import { toast } from "react-toastify";
import { VideoSource } from "../../Enums/enums";
import { sourceAfterBatch, tabAfterRemoval } from "./batchItems";

const SOURCE_NAMES = { [VideoSource.ORIGINAL]: "Original", [VideoSource.LOCAL]: "Local upload", [VideoSource.DUPLICATE]: "Duplicate" };

/**
 * Removing tabs picked by mistake. A removed PLATFORM tab's card leaves
 * Edited for the source it had before (it still publishes, just unedited) and
 * loses its saved edit. A removed VIDEO (WGID) tab takes that video out of the
 * publish altogether — deselected in Step 2, gone from Step 3 (user request
 * 2026-10-13) — when the wizard passes `onRemoveVideo`. Removing the open tab
 * moves on: the next platform of this video, else the next video; with
 * nothing left the batch editor closes back to Step 3.
 *
 * Returns `{ removePlatformTab(item), removeVideoTab(videoKey) }`.
 */
export default function useTabRemoval({ items, activeItem, hasUnsaved, onConfigChange, onRemoveVideo, setMark, clearSaveNote, askConfirm, goTo, onClose }) {
  const removeTabs = (removed, label, wholeVideo = false) => {
    const deselect = wholeVideo && Boolean(onRemoveVideo);
    if (removed.length === 0) return;
    const keys = removed.map((i) => i.key);
    const removesActive = Boolean(activeItem && keys.includes(activeItem.key));
    const sources = [...new Set(removed.map((i) => SOURCE_NAMES[sourceAfterBatch(i)] || sourceAfterBatch(i)))];
    const backTo = sources.length === 1 ? sources[0] : "their earlier source";
    const apply = () => {
      const next = removesActive ? tabAfterRemoval(items, keys, activeItem) : activeItem;
      if (deselect) {
        onRemoveVideo(removed[0].brandName, removed[0].wgid);
      } else {
        removed.forEach((i) =>
          onConfigChange(i.brandName, i.wgid, i.platform, {
            source: sourceAfterBatch(i),
            renderPayload: null,
            sourceBeforeBatch: null,
            batchApplied: null,
          }),
        );
      }
      if (removesActive) {
        setMark(activeItem.key, null);
        clearSaveNote();
      }
      toast.info(
        deselect
          ? `${label} removed — no longer selected in Steps 2 and 3, and will not be published.`
          : `${label} removed from the batch editor — set back to ${backTo} in Step 3.`,
      );
      if (!next) onClose();
      else if (removesActive) goTo(next.key);
    };
    const unsavedLost = removesActive && hasUnsaved;
    const savedLost = removed.filter((i) => i.config?.renderPayload);
    if (deselect) {
      // Taking a video out of the publish always asks: it undoes a Step 2 choice.
      const lost = [];
      if (unsavedLost) lost.push(`${activeItem.platform} has unsaved edits that will be lost.`);
      if (savedLost.length > 0) lost.push(`The saved edit${savedLost.length !== 1 ? "s" : ""} on ${savedLost.map((i) => i.platform).join(", ")} will be deleted.`);
      lost.push("It is deselected in Step 2, removed from Step 3, and will not be published. You can select it again in Step 2.");
      askConfirm(`Remove ${label} from this publish?`, lost.join(" "), "Remove video", apply);
      return;
    }
    if (!unsavedLost && savedLost.length === 0) {
      apply();
      return;
    }
    const parts = [];
    if (unsavedLost) parts.push(`${activeItem.platform} has unsaved edits that will be lost.`);
    if (savedLost.length > 0) parts.push(`The saved edit${savedLost.length !== 1 ? "s" : ""} on ${savedLost.map((i) => i.platform).join(", ")} will be deleted.`);
    parts.push(`${removed.length === 1 ? "The card goes" : "The cards go"} back to ${backTo} in Step 3 and will still be published.`);
    askConfirm(`Remove ${label} from the batch editor?`, parts.join(" "), "Remove", apply);
  };

  return {
    removePlatformTab: (item) => removeTabs([item], `${item.platform} of ${item.wgid}`),
    removeVideoTab: (videoKey) => {
      const removed = items.filter((i) => i.videoKey === videoKey);
      if (removed.length > 0) removeTabs(removed, removed[0].wgid, true);
    },
  };
}
